import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Login from './Login';

// Session timeout: 8 hours (matches backend JWT expiry)
const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = useCallback(() => {
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');

    // Remove token from API headers
    delete api.defaults.headers.common['Authorization'];

    setIsAuthenticated(false);
  }, []);

  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const loginTime = localStorage.getItem('loginTime');

    if (!token || !loginTime) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    // Check if session has expired
    const elapsed = Date.now() - parseInt(loginTime);
    if (elapsed > SESSION_TIMEOUT) {
      console.log('⏰ Session expired');
      handleLogout();
      setIsChecking(false);
      return;
    }

    // Set token in API headers
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    setIsAuthenticated(true);
    setIsChecking(false);
  }, [handleLogout]);

  const checkSessionTimeout = useCallback(() => {
    const loginTime = localStorage.getItem('loginTime');
    if (!loginTime) return;

    const elapsed = Date.now() - parseInt(loginTime);
    if (elapsed > SESSION_TIMEOUT) {
      console.log('⏰ Session expired due to inactivity');
      handleLogout();
    }
  }, [handleLogout]);

  useEffect(() => {
    checkAuthentication();

    // Set up automatic session timeout check
    const timeoutCheck = setInterval(() => {
      checkSessionTimeout();
    }, 60000); // Check every minute

    return () => clearInterval(timeoutCheck);
  }, [checkAuthentication, checkSessionTimeout]);

  // Expose logout function globally so FrontDesk can use it
  if (isAuthenticated && children) {
    return React.cloneElement(children, { onLogout: handleLogout });
  }

  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#718096'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return children;
}

export default ProtectedRoute;
